import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Intervalle de vérification de version (30 minutes)
const VERSION_CHECK_INTERVAL = 30 * 60 * 1000;

/**
 * Composant qui gère les mises à jour automatiques de l'application.
 * 
 * Fonctionnement :
 * 1. Vérifie la version serveur toutes les 30 minutes + à l'ouverture
 * 2. Si la version a changé, recharge automatiquement (silencieusement)
 * 3. Gère aussi les mises à jour du Service Worker
 */
const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // Fonction pour nettoyer le cache et recharger
  const forceRefresh = useCallback(async () => {
    console.log('[UpdateNotification] Forçage de la mise à jour...');
    
    // Nettoyer tous les caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('[UpdateNotification] Caches nettoyés');
      } catch (e) {
        console.error('[UpdateNotification] Erreur nettoyage cache:', e);
      }
    }
    
    // Si un Service Worker est en attente, lui dire de prendre le contrôle
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Forcer le rechargement
    window.location.reload(true);
  }, [waitingWorker]);

  // Vérification de la version serveur
  const checkServerVersion = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/app-version`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store' // Pas de cache pour cette requête
      });
      
      if (!response.ok) {
        console.log('[UpdateNotification] Impossible de vérifier la version');
        return;
      }
      
      const data = await response.json();
      const serverVersion = data.version;
      const storedVersion = localStorage.getItem('app_version');
      
      console.log(`[UpdateNotification] Version serveur: ${serverVersion}, Version locale: ${storedVersion || 'aucune'}`);
      
      if (!storedVersion) {
        // Première visite, enregistrer la version actuelle
        localStorage.setItem('app_version', serverVersion.toString());
        console.log('[UpdateNotification] Première visite, version enregistrée');
        return;
      }
      
      if (parseInt(storedVersion) < serverVersion) {
        // Nouvelle version disponible ! Mettre à jour automatiquement
        console.log('[UpdateNotification] Nouvelle version détectée ! Mise à jour automatique...');
        localStorage.setItem('app_version', serverVersion.toString());
        
        // Recharger automatiquement (silencieusement)
        await forceRefresh();
      }
    } catch (error) {
      console.log('[UpdateNotification] Erreur vérification version:', error);
      // En cas d'erreur (hors ligne, etc.), on ne fait rien
    }
  }, [forceRefresh]);

  useEffect(() => {
    // Vérifier la version au chargement
    checkServerVersion();
    
    // Configurer la vérification périodique (toutes les 30 minutes)
    const versionCheckInterval = setInterval(checkServerVersion, VERSION_CHECK_INTERVAL);
    
    // Vérifier aussi quand l'application revient au premier plan
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkServerVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Gestion du Service Worker (existant)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        // Vérifier si une mise à jour est en attente
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowUpdate(true);
        }

        // Écouter les nouvelles mises à jour
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Nouvelle version disponible
                setWaitingWorker(newWorker);
                setShowUpdate(true);
              }
            });
          }
        });
      });

      // Vérifier les mises à jour du SW toutes les 2 minutes
      const swCheckInterval = setInterval(() => {
        navigator.serviceWorker.ready.then(registration => {
          registration.update();
        });
      }, 2 * 60 * 1000);

      // Rafraîchir automatiquement quand le SW prend le contrôle
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });

      return () => {
        clearInterval(versionCheckInterval);
        clearInterval(swCheckInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
    
    return () => {
      clearInterval(versionCheckInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkServerVersion]);

  const handleUpdate = async () => {
    setShowUpdate(false);
    await forceRefresh();
  };

  // Notification visible seulement si mise à jour Service Worker en attente
  // (les mises à jour de version serveur sont automatiques et silencieuses)
  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-[#1F3A8A] text-white rounded-2xl shadow-2xl p-4 flex items-center gap-4">
        <div className="p-2 bg-white/20 rounded-xl">
          <RefreshCw className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <p className="font-semibold">Mise à jour disponible !</p>
          <p className="text-sm text-blue-200">Cliquez pour actualiser</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleUpdate}
            className="bg-white text-[#1F3A8A] hover:bg-blue-50 font-semibold"
            size="sm"
          >
            Actualiser
          </Button>
          <button
            onClick={() => setShowUpdate(false)}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;
